import moment from 'moment-timezone';

import { reminderNeedsAttention } from '../../../../pages/task/TaskReminders/reminderSummary';

const asId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0
    ? id
    : null;
};

const getLocalMoment = (value, timezone) => {
  if (timezone && moment.tz.zone(timezone)) return moment.tz(value, timezone);
  return moment(value);
};

export const getPlannerReminderDates = (now = new Date(), timezone = null) => {
  const localNow = getLocalMoment(now, timezone);
  const today = localNow.clone().startOf('day');
  const schedulingDate = today.clone();
  if (localNow.hour() >= 17) schedulingDate.add(1, 'day');
  return {
    today: today.toDate(),
    schedulingDate: schedulingDate.toDate(),
  };
};

const getUpcomingSchedule = (reminder, now) => {
  const schedule = reminder.nextSchedule;
  if (!asId(schedule?.id) || schedule.archived || !schedule.nextOccurrenceDate) return null;
  const nextDate = moment(schedule.nextOccurrenceDate, moment.ISO_8601, true);
  const endDate = moment(schedule.endDate, moment.ISO_8601, true);
  const withinSchedule = !schedule.endDate || (endDate.isValid() && nextDate.isSameOrBefore(endDate));
  return nextDate.isValid() && nextDate.isAfter(now) && withinSchedule
    ? schedule
    : null;
};

const isMatchingTask = (item, tagId, templateId) => {
  if (item.archived || item.task?.archived || item.event?.resource?.archived) return false;
  if (item.task) return asId(item.task.eventTemplateId) === templateId;
  const workflows = item.event?.resource?.workflows;
  if (Array.isArray(workflows)) {
    return workflows.some((workflow) => asId(workflow.eventTemplateId) === templateId
      && workflow.tagIds?.some((id) => asId(id) === tagId));
  }
  return !item.calendarEvent && asId(item.taskTypeId) === templateId;
};

const actionPriority = {
  complete: 0,
  review: 1,
  reschedule: 2,
  schedule: 3,
};

const dueTimestamp = (reminder) => {
  const timestamp = new Date(reminder.dueDate || '').getTime();
  return Number.isFinite(timestamp)
    ? timestamp
    : Infinity;
};

const compareSuggestions = (left, right) => actionPriority[left.action] - actionPriority[right.action]
  || dueTimestamp(left.reminder) - dueTimestamp(right.reminder)
  || Number(left.reminder.id) - Number(right.reminder.id);

const isHorsePresent = (horse, date, timezone) => {
  const day = getLocalMoment(date, timezone);
  if (horse.startDate) {
    const arrival = getLocalMoment(horse.startDate, timezone);
    if (arrival.isValid() && day.isBefore(arrival.startOf('day'))) return false;
  }
  if (horse.endDate) {
    const departure = getLocalMoment(horse.endDate, timezone);
    if (departure.isValid() && day.isAfter(departure.endOf('day'))) return false;
  }
  return true;
};

const buildPlannerReminderSuggestions = ({
  reminders = [],
  barnHorses = [],
  overview = {},
  tasks = [],
  now = new Date(),
  timezone,
  eventTemplateIds = [],
  filterTagIds = [],
} = {}) => {
  const horsesByTagId = new Map(barnHorses
    .filter((horse) => !horse.inactive && !horse.archived && !horse.horse?.archived && asId(horse.pingumTagId))
    .map((horse) => [asId(horse.pingumTagId), horse]));
  const tasksById = new Map(tasks.filter((task) => asId(task.id)).map((task) => [asId(task.id), task]));
  const selectedTemplates = new Set((eventTemplateIds || []).map(asId).filter(Boolean));
  const selectedTags = new Set((filterTagIds || []).map(asId).filter(Boolean));
  const dates = getPlannerReminderDates(now, timezone);
  const candidates = [];

  reminders.forEach((reminder) => {
    const definition = reminder?.eventTemplateReminder;
    const template = definition?.eventTemplate;
    const templateId = asId(template?.id);
    const tagId = asId(reminder?.tag?.id);
    const barnHorse = horsesByTagId.get(tagId);
    if (!barnHorse || !templateId || !asId(reminder?.id) || reminder.archived || reminder.tag.archived
      || definition.archived || template.archived) return;
    if (selectedTemplates.size && !selectedTemplates.has(templateId)) return;

    const savedLastEvent = reminder.lastEvent;
    const taskId = asId(savedLastEvent?.id);
    const lastEvent = tasksById.get(taskId) || savedLastEvent;
    const savedUnfinished = taskId && !savedLastEvent.completeDate && !savedLastEvent.archived;
    // A newer board update takes precedence while the reminder response catches up.
    if (savedUnfinished && (lastEvent?.completeDate || lastEvent?.archived)) return;
    const unfinishedTask = taskId && !lastEvent.completeDate && !lastEvent.archived
      ? lastEvent
      : null;
    if (!unfinishedTask && !reminderNeedsAttention(reminder, now)) return;

    let action = 'schedule';
    let date = dates.schedulingDate;
    let schedule = null;
    if (unfinishedTask) {
      action = 'complete';
      date = dates.today;
    } else if (reminder.status === 'Incomplete') {
      action = 'review';
      date = dates.today;
    } else {
      schedule = getUpcomingSchedule(reminder, now);
      if (schedule) action = 'reschedule';
    }
    const matchingTags = unfinishedTask?.tags || schedule?.tags || template.tags;
    if (selectedTags.size && !matchingTags?.some((tag) => selectedTags.has(asId(tag.id)))) return;
    const dayKey = String(date.getTime());
    candidates.push({
      id: `planner-reminder-${tagId}-${templateId}-${dayKey}`,
      reminder,
      barnHorse,
      template,
      task: unfinishedTask,
      schedule,
      action,
      dayKey,
      date,
    });
  });

  const suggestions = {};
  const selectedPairs = new Set();
  candidates.sort(compareSuggestions).forEach((suggestion) => {
    const tagId = asId(suggestion.barnHorse.pingumTagId);
    const templateId = asId(suggestion.template.id);
    const pairKey = `${tagId}:${templateId}`;
    if (selectedPairs.has(pairKey)) return;
    selectedPairs.add(pairKey);
    if (!isHorsePresent(suggestion.barnHorse, suggestion.date, timezone)) return;
    const dayItems = overview[tagId]?.[suggestion.dayKey];
    if (!Array.isArray(dayItems)) return;
    const alreadyVisible = suggestion.task
      ? dayItems.some((item) => !item.archived && !item.task?.archived && asId(item.task?.id) === asId(suggestion.task.id))
      : dayItems.some((item) => isMatchingTask(item, tagId, templateId));
    if (alreadyVisible) return;
    if (!suggestions[tagId]) suggestions[tagId] = {};
    if (!suggestions[tagId][suggestion.dayKey]) suggestions[tagId][suggestion.dayKey] = [];
    suggestions[tagId][suggestion.dayKey].push(suggestion);
  });
  return suggestions;
};

export default buildPlannerReminderSuggestions;
